/**
 * AdminSupplies — bucket 4 of 6. Renders the design's three-module national
 * supply layout: Regional Inventory Health, Critical SKUs · Nationwide,
 * Procurement Schedule.
 *
 * Every module depends on the inventory schema, which doesn't exist yet (no
 * op-DB or warehouse source — it's a separate Project). The whole surface
 * therefore renders the design layout as an em-dash skeleton behind a single
 * <MockedOverlay>, mirroring RegionalSupplies. It lights up when BON-151 lands.
 */
import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, MockedOverlay } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';

import { useAdminSupplies } from './adminApi';

const EM_DASH = '—';

const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;
const HEALTH_COLS = [
  'Region',
  'Venues',
  'Audits submitted',
  'Outstanding',
  'Low-stock SKUs',
  'Next resupply',
  'Status',
] as const;

const cardHeading: React.CSSProperties = {
  fontSize: 13,
  letterSpacing: '0.04em',
  margin: 0,
};

function RegionalInventoryHealthCard() {
  return (
    <section
      className="nb-card nb-card-tight"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px 8px' }}>
        <h3 className="nb-display" style={cardHeading}>
          Regional Inventory Health
        </h3>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
          by audit submission &amp; low-stock SKUs
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table className="nb-tbl">
          <thead>
            <tr>
              {HEALTH_COLS.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SKELETON_KEYS.map((k) => (
              <tr key={k}>
                {HEALTH_COLS.map((c) => (
                  <td key={c} style={{ color: 'var(--nb-subtle)' }}>
                    {EM_DASH}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CriticalSkusCard() {
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={cardHeading}>
          Critical SKUs · Nationwide
        </h3>
        <span
          className="nb-pill nb-status-soft"
          style={{ height: 18, fontSize: 9.5, padding: '0 6px' }}
        >
          {EM_DASH} critical
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {SKELETON_KEYS.slice(0, 4).map((k) => (
          <div key={k}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 3,
              }}
            >
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--nb-subtle)' }}>
                {EM_DASH}
              </span>
              <span
                className="nb-tabular nb-mono"
                style={{ fontSize: 10.5, color: 'var(--nb-subtle)' }}
              >
                {EM_DASH}
              </span>
            </div>
            <div className="nb-bar" style={{ width: '100%' }}>
              <i style={{ width: '0%' }} />
            </div>
            <div style={{ fontSize: 10, color: 'var(--nb-subtle)', marginTop: 2 }}>{EM_DASH}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProcurementScheduleCard() {
  return (
    <section className="nb-card" style={{ flex: 1, minHeight: 0 }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={cardHeading}>
          Procurement Schedule
        </h3>
        <span className="nb-eyebrow">Q2 plan</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {SKELETON_KEYS.slice(0, 4).map((k) => (
          <div
            key={k}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 0',
              borderBottom: '1px dashed var(--nb-border)',
              color: 'var(--nb-subtle)',
            }}
          >
            <span style={{ width: 50, fontSize: 11, fontWeight: 700 }}>{EM_DASH}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600 }}>{EM_DASH}</div>
              <div style={{ fontSize: 10.5 }}>{EM_DASH}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AdminSupplies() {
  const q = useAdminSupplies();
  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <MockedOverlay reason="National inventory & procurement data isn’t wired up yet. Goes live once the inventory module ships — tracked in BON-151.">
      <KpiStrip items={adaptKpis(data.kpis)} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
          gap: 14,
          marginTop: 16,
        }}
      >
        <RegionalInventoryHealthCard />
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, minHeight: 0 }}
        >
          <CriticalSkusCard />
          <ProcurementScheduleCard />
        </div>
      </div>
    </MockedOverlay>
  );
}
